import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Scenario } from 'src/app/care/scenarios/models';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { NavigationModeEnum, ScenarioFlavorEnum } from 'src/app/metrics/shared/enums';
import { Metric, Metrics, ScenarioSimpleInfo } from 'src/app/metrics/shared/models';

import { UsersForStats } from '../models';
import { API_URL_COMPANIES, API_URL_COMPANIES_V2 } from './basic.service';
import { ErrorHandlingService } from './error-handling.service';

@Injectable({
  providedIn: 'root',
})
export class MetricsService {
  constructor(
    private httpClient: HttpClient,
    private alcmeonContextService: AlcmeonContextService,
    private errorHandlingService: ErrorHandlingService
  ) {}

  getCareMetrics() {
    const params = new HttpParams().set('mode', 'care');
    return this.httpClient
      .get<any[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/metrics`, { params })
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getScenarios(scenariosFlavor: 'campaign-scenarios'): Observable<Scenario[]> {
    return this.httpClient
      .get<Scenario[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/scenarios`, {
        params: { flavor: scenariosFlavor },
      })
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  /**
   * @param isVerbatim = true only works with satisfaction-survey flavor to add data to scenario_verbatim_details
   */
  getScenariosSimplifiedInfo(
    scenariosFlavor: ScenarioFlavorEnum,
    isVerbatim?: boolean
  ): Observable<ScenarioSimpleInfo[]> {
    const queryParams = isVerbatim ? { flavor: scenariosFlavor, verbatim: isVerbatim } : { flavor: scenariosFlavor };

    return this.httpClient
      .get<ScenarioSimpleInfo[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/scenario-simple-info`, {
        params: queryParams,
      })
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getMetrics(navigationMode: NavigationModeEnum): Observable<Metrics[]> {
    return this.httpClient
      .get<Metrics[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/metrics`, {
        params: { mode: navigationMode },
      })
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getMetric(metricId: number): Observable<Metric> {
    return this.httpClient
      .get<Metric>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/metrics/${metricId}`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  /**
   * Get an object of routing tags ids, where value is a boolean showing the use of an AIAgent or not.
   * The goal is to build a team selector, hierarchized by "Using AI Agent" and "Not using AI Agent".
   */
  getRoutingTagsUsesAi(): Observable<{ [id: number]: boolean }> {
    return this.httpClient
      .get<{ [id: number]: boolean }>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/routing-tags-for-stats`
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getUsersForStats(): Observable<UsersForStats> {
    return this.httpClient
      .get<UsersForStats>(`${API_URL_COMPANIES_V2}/${this.alcmeonContextService.companyId}/users-for-stats`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }
}
